export type SwipeDirection = 'left' | 'right'

const THRESHOLD_PX = 40

// The root of the tree the element is in right now — the document, once it
// is mounted. Resolved per gesture rather than at attach time: a node cloned
// from a <template> is owned by the template's inert document until it is
// inserted, so a release listener put on `ownerDocument` at attach never
// hears a thing.
function liveRoot(element: HTMLElement): Node {
  let root: Node = element
  while (root.parentNode) root = root.parentNode
  return root
}

// A discrete horizontal swipe over an element (GLOSSARY.md "Detail slide",
// ported from Shop's lib/swipe.ts): the pointer goes down inside it and
// comes up — anywhere in the document — at least 40px away horizontally, and
// further sideways than up or down. Nothing follows the finger in between;
// the gesture is judged whole at release, so the element's own layout never
// moves under a drag. One gesture at a time: the pointer that started it is
// the one judged, so a second finger can neither end nor hijack it. Returns
// the detach.
export function listenForSwipe(
  element: HTMLElement,
  onSwipe: (direction: SwipeDirection) => void,
): () => void {
  let gesture:
    | { pointerId: number; root: Node; x: number; y: number }
    | undefined

  const end = () => {
    if (!gesture) return
    gesture.root.removeEventListener('pointerup', onPointerUp)
    gesture.root.removeEventListener('pointercancel', onPointerCancel)
    gesture = undefined
  }
  const onPointerUp: EventListener = (event) => {
    const { clientX, clientY, pointerId } = event as PointerEvent
    if (!gesture || pointerId !== gesture.pointerId) return
    const dx = clientX - gesture.x
    const dy = clientY - gesture.y
    end()
    if (Math.abs(dx) >= THRESHOLD_PX && Math.abs(dx) > Math.abs(dy)) {
      onSwipe(dx < 0 ? 'left' : 'right')
    }
  }
  const onPointerCancel: EventListener = (event) => {
    if (gesture && (event as PointerEvent).pointerId === gesture.pointerId) {
      end()
    }
  }
  const onPointerDown = (event: PointerEvent) => {
    if (gesture) return
    const root = liveRoot(element)
    gesture = {
      pointerId: event.pointerId,
      root,
      x: event.clientX,
      y: event.clientY,
    }
    root.addEventListener('pointerup', onPointerUp)
    root.addEventListener('pointercancel', onPointerCancel)
  }

  element.addEventListener('pointerdown', onPointerDown)
  return () => {
    element.removeEventListener('pointerdown', onPointerDown)
    end()
  }
}
